/**
 * AffaireSelector Component
 * Allows user to search and select an affaire for appreciation generation
 * Manual search with list-based results
 */

import React, { useState, useMemo } from 'react'
import { MagnifyingGlassIcon, XMarkIcon, SparklesIcon, FolderOpenIcon } from '@heroicons/react/24/outline'
import { useSearchAffaire, useAppreciation } from '../hooks/useIA'
import type { SearchAffaire } from '../types/ia.types'

interface AffaireSelectorProps {
  selectedAffaireId: number | null
  onAffaireSelect: (affaireId: number, adresse: string) => void
  onGenerateClick: (affaireId: number, adresse: string) => Promise<void>
  generatingAffaires: Set<number>
}

export const AffaireSelector: React.FC<AffaireSelectorProps> = ({
  selectedAffaireId,
  onAffaireSelect,
  onGenerateClick,
  generatingAffaires
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchTrigger, setSearchTrigger] = useState('')

  const { data: searchResults, isLoading: loadingSearch, error: errorSearch } = useSearchAffaire(
    searchTrigger,
    searchTrigger.trim().length > 0
  )

  // Fetch appreciation data for selected affaire
  const { data: appreciationData } = useAppreciation(selectedAffaireId)

  // Deduplicate search results by id_unique
  const deduplicatedResults = useMemo(() => {
    if (!searchResults) return null

    if (searchResults.affaire) {
      return {
        affaire: searchResults.affaire,
        matchType: searchResults.matchType
      }
    }

    if (searchResults.affaires && searchResults.affaires.length > 0) {
      // Deduplicate by id_unique
      const uniqueMap = new Map<string, SearchAffaire>()
      searchResults.affaires.forEach(affaire => {
        if (!uniqueMap.has(affaire.id_unique)) {
          uniqueMap.set(affaire.id_unique, affaire)
        }
      })
      return {
        affaires: Array.from(uniqueMap.values()),
        matchType: searchResults.matchType
      }
    }

    return searchResults
  }, [searchResults])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim().length > 0) {
      setSearchTrigger(searchQuery.trim())
    }
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    setSearchTrigger('')
  }

  // Clear search results when input is cleared
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchQuery(value)

    // If input is cleared, also clear the search results
    if (value.trim().length === 0) {
      setSearchTrigger('')
    }
  }

  const formatAddress = (address: any): string => {
    if (!address) return ''
    return [
      address.street,
      address.number,
      address.postcode,
      address.locality
    ].filter(Boolean).join(' ')
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  // Get all affaires to display (single or multiple)
  const affairesToDisplay = useMemo(() => {
    if (!deduplicatedResults) return []

    if (deduplicatedResults.affaire) {
      return [deduplicatedResults.affaire]
    }

    if (deduplicatedResults.affaires) {
      return deduplicatedResults.affaires
    }

    return []
  }, [deduplicatedResults])

  const hasAppreciation = appreciationData?.appreciation && appreciationData.appreciation.trim().length > 0

  return (
    <div className="w-full bg-white rounded-2xl shadow-md border-2 border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200">
        <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
          <MagnifyingGlassIcon className="h-5 w-5 text-blue-600" />
          Sélection de l'affaire
        </h2>
        <p className="text-sm text-gray-600">
          Recherchez une affaire par ID ou adresse
        </p>
      </div>

      {/* Manual Search Input */}
      <div className="p-5 bg-gradient-to-br from-white to-gray-50">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Recherche manuelle
        </label>
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="ID affaire (ex: 12345) ou adresse..."
              value={searchQuery}
              onChange={handleInputChange}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
          <button
            type="submit"
            disabled={loadingSearch || !searchQuery.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingSearch ? 'Recherche...' : 'Rechercher'}
          </button>
        </form>

        {/* Search Results */}
        {searchTrigger && (
          <div className="mt-4">
            {loadingSearch && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm text-gray-600">Recherche en cours...</span>
              </div>
            )}

            {errorSearch && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-red-800 text-sm">⚠️ Aucune affaire trouvée</p>
              </div>
            )}

            {affairesToDisplay.length > 0 && !loadingSearch && (
              <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
                {/* Results Header */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-3 border-b border-green-200">
                  <p className="text-sm font-semibold text-gray-900">
                    {affairesToDisplay.length} affaire{affairesToDisplay.length > 1 ? 's' : ''} trouvée{affairesToDisplay.length > 1 ? 's' : ''}
                  </p>
                </div>

                {/* Results List */}
                <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                  {affairesToDisplay.map((affaire) => {
                    const affaireId = parseInt(affaire.id_unique, 10)
                    const isGenerating = generatingAffaires.has(affaireId)
                    const adresse = formatAddress(affaire.address)

                    return (
                      <div
                        key={affaire.id_unique}
                        className="p-4 flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-500 flex-shrink-0">
                              #{affaire.id_unique}
                            </span>
                            <span className="text-sm text-gray-900 truncate">
                              {adresse || 'Adresse non disponible'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {affaire.type_mission && (
                              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                                {affaire.type_mission}
                              </span>
                            )}
                            {affaire.date_sortie && (
                              <span className="text-xs text-gray-500">
                                {formatDate(affaire.date_sortie)}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex-shrink-0 flex items-center gap-2">
                          <button
                            onClick={() => onAffaireSelect(affaireId, adresse || `Affaire #${affaire.id_unique}`)}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-all duration-150 shadow-sm"
                          >
                            <FolderOpenIcon className="w-4 h-4" />
                            Ouvrir
                          </button>

                          <button
                            onClick={() => onGenerateClick(affaireId, adresse || `Affaire #${affaire.id_unique}`)}
                            disabled={isGenerating}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 active:bg-indigo-800 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md min-w-[110px]"
                          >
                            {isGenerating ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                En cours...
                              </>
                            ) : (
                              <>
                                <SparklesIcon className="w-4 h-4" />
                                Générer
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
